import React from 'react';
import Proptypes from 'prop-types';
import _ from 'underscore';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';

import { Input, Icon, Dropdown } from 'semantic-ui-react';
import Paging from './Paging';
import Table from './Table';

// Redux
import { withStateHandlers, withHandlers, compose } from 'recompose';

// Misc
import * as IcFeature from '../../../images/features';
import { escapeRegExp } from '../FormRules';
import { translateToSemanticDropdown as translateOptions } from '../../Translator';
import { getIconFromName } from './Misc';
import { log } from '../../Misc';

const TableActionButton = ({
    index,
    className,
    rows,
    checkboxes = [],
    table,
    img: { title, name, image },
    onClick,
}) => (
    <div
        key={index}
        className={['table__action--default ui label', className].join(' ')}
        onClick={e => {
            rows = rows || {};
            const checkedRows = [];
            const checkedIndex = [];

            Object.entries(checkboxes).forEach((checkbox, index) => {
                const [, isChecked] = checkbox;
                if (isChecked) {
                    checkedIndex.push(index);
                    checkedRows.push(rows[index]);
                }
            });

            typeof onClick === 'function' &&
                onClick(e, { checkedRows, checkedIndex, table: table });
        }}>
        <img className="action__icon" src={image} alt={name} title={title} />
    </div>
);

const TableEditButton = ({ index, activate, onClick }) => (
    <TableActionButton
        index={index}
        className={activate ? 'table__action--default-activated' : ''}
        img={{
            image: IcFeature.IconEdit,
            name: 'more',
            title: 'More Actions',
        }}
        onClick={onClick}
    />
);

/**
 * TableWrapper Component that will render table with
 * search bar, paging, and extra columns for No. and Actions.
 * No. column is auto generated.
 * Actions column is generated only if columnActions
 * prop is passed, formatted as, columnActions: { header: { title, name }, actions: Array }
 *
 * @class Table
 * @extends {React.Component}
 */
class TableWrapper extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isEditing: false,
            searchString: props.initialSearch || '',
            searchableColumns: [],
            headerCheckboxes: {},
            columnCheckboxes: {},
        };

        this.firstTimeUpdate = true;
    }

    componentDidMount() {
        const {
            data = {},
            setFilteredRows,
            recordNumberOffset = 0,
            initialSearch,
            setSearchString,
            setPageNumber,
            searchPage,
        } = this.props;
        if (initialSearch) {
            setSearchString(initialSearch);
            setPageNumber(searchPage.number, true);
        }
        if (data.rows.length) {
            setFilteredRows(setRecordNumberIntoRows(data.rows, recordNumberOffset), false);
        }
    }

    componentDidUpdate(prevProps) {
        const {
            data = {},
            searchString,
            sortingHeaderName,
            sortingAsc,

            handleFilter,
            setFilteredRows,
            recordNumberOffset,

            setPageNumber,
            initialPage,
        } = this.props;
        const firstTimeUpdate = this.firstTimeUpdate;
        this.firstTimeUpdate = false;

        const {
            data: prevData,
            sortingHeaderName: prevSortingHeaderName,
            sortingAsc: prevSortingAsc,
            searchString: prevSearchString,
        } = prevProps;
        const isEqualSearchStr = searchString === prevSearchString;
        const isEqualHeaderName = sortingHeaderName === prevSortingHeaderName;
        const isEqualSortingAsc = sortingAsc === prevSortingAsc;
        const isEqualSearchObj = _.isEqual(data.search, prevData.search);

        if (
            data.rows &&
            (!_.isEqual(data.rows, prevData.rows) ||
                !_.isEqual(prevProps.recordNumberOffset, recordNumberOffset))
        ) {
            setFilteredRows(setRecordNumberIntoRows(data.rows, recordNumberOffset), !!searchString);
            handleFilter();

            // new fresh data from empty should consider setting the initialPage
            if (!prevData.rows?.length && data.rows?.length) {
                if (initialPage) {
                    setPageNumber(initialPage, !!searchString);
                }
            }
            return;
        }
        // fitler / search / sort
        if (!isEqualSearchStr || !isEqualSearchObj || !isEqualSortingAsc || !isEqualHeaderName) {
            handleFilter();
            if (firstTimeUpdate && initialPage) {
                setPageNumber(initialPage, !!searchString);
            }
        }
    }

    getTableActionComponents({ columnActions = {}, tableActions, isEditing }) {
        let generalTableActionComponent = [],
            tableActionComponent = [];
        const actions = _.isObject(columnActions) && columnActions.actions;
        // add table's action
        const isExistTableAction = _.filter(actions, action => action.isTableAction).length > 0;
        generalTableActionComponent =
            isEditing && isExistTableAction
                ? Object.entries(actions).map((button, index) => {
                      let [, value] = button;
                      value = value || {};
                      let { title, name, icon, onTableActionClick, isTableAction } = value;
                      const { rows } = this.props.data || {};
                      const image = image || getIconFromName(name) || icon;
                      name = name || title || '';
                      const imgOption = {
                          image,
                          name,
                          title,
                      };
                      onTableActionClick =
                          onTableActionClick ||
                          (e => {
                              e.preventDefault();
                          });

                      if (isTableAction)
                          return (
                              <TableActionButton
                                  index={index}
                                  rows={rows}
                                  img={imgOption}
                                  checkboxes={this.state.columnCheckboxes}
                                  onClick={onTableActionClick}
                              />
                          );

                      return;
                  })
                : [];
        generalTableActionComponent =
            isExistTableAction &&
            generalTableActionComponent.concat([
                <TableEditButton
                    key="edit"
                    index="edit"
                    activate={isEditing}
                    onClick={this.handleTableEditClick.bind(this)}
                />,
            ]);

        tableActionComponent = tableActionComponent.concat(generalTableActionComponent);
        tableActionComponent = tableActionComponent.concat(
            Object.entries(tableActions).map((action, index) => {
                const [, value] = action;
                const { icon, title, name, onClick } = value;
                const iconPath = getIconFromName(icon);
                const img = {
                    image: iconPath,
                    name,
                    title,
                };
                return (
                    <TableActionButton
                        key={index}
                        index={index}
                        img={img}
                        onClick={e => {
                            onClick && onClick(e, { value: title || name || icon });
                        }}
                    />
                );
            }),
        );

        return tableActionComponent;
    }

    handleSearchTextChanged(e, value) {
        this.props.setSearchString(value);
    }

    handleOptionChanged(e, data) {
        data = data || {};
        const action = data.action;
        const value = data.value;

        switch (action) {
            case 'search':
                this.onSearchColumnChanged(e, value);
                break;
            default:
        }
    }

    handleTableEditClick() {
        const { isEditing } = this.state;

        this.setState({
            isEditing: !isEditing,
        });
    }

    // TO-CONSIDER: check is this still being used? doesn't appear so.
    handlePageChanged(e, number) {
        const { page } = this.state;
        this.setState({
            page: {
                ...page,
                number,
            },
        });
    }

    handleHeaderClick(e, { name, asc, index }) {
        this.props.setSortingAsc(asc);
        this.props.setSortingHeaderName(name);
        this.props.setSortingHeaderIndex(index);
    }

    onSearchColumnChanged(e, value) {
        this.setState({
            searchableColumns: value,
        });
    }

    render() {
        let {
            // Replacing Component,
            Row,
            EmptyRow,
            Header,
            Footer,
            Content = Table,
            isTable = true,

            className,
            data = {},
            page: _page = {},
            searchPage = {},
            links = {},
            columnActions = {},
            tableActions = {},
            columnCheckboxes = {},
            headerCheckboxes = {},
            totalPages = 0,
            filteredRows = [],
            sortingHeaderIndex,
            sortingHeaderName,
            sortingAsc,
            childrenHeader,
            childrenFooter,
            searchEnabled = true,
            isSearching = false,
            initialSearch,
            searchString,
            searchPageCallback,
            paginationEnabled = true,

            // handler
            setPageNumber,
        } = this.props;
        if (Object.keys(this.props).length === 0) {
            return null;
        }
        const { title, description, search = {}, headers } = data;
        const page = isSearching ? searchPage : _page;
        const searchDropdown = search.options || {};
        let searchDropdownOptions = searchDropdown.options || {};
        const translateTo = {
            name: ['key', 'value'],
            title: 'text',
        };
        // remove option that appears in search.match
        searchDropdownOptions = _.filter(searchDropdownOptions, option => {
            return search.match[option.name] === undefined;
        });
        // translate options to required format { title, name } => { key, text, value }
        searchDropdownOptions = translateOptions({
            options: searchDropdownOptions,
            translateTo,
        });

        const { isEditing } = this.state;
        const tableActionComponent = this.getTableActionComponents({
            columnActions,
            tableActions,
            isEditing,
        });

        let showingRows = Object.entries(filteredRows);

        let pgNum = page.number;
        // pagination
        if (paginationEnabled) {
            // still show clamped view
            if (pgNum < 1) {
                pgNum = 1;
            } else if (pgNum > totalPages) {
                pgNum = totalPages;
            }
            const first = (pgNum - 1) * page.rows;
            const second = first + page.rows;
            showingRows = showingRows.slice(first, second);
        }

        // sorting
        const sortingHeaders = setSortingIntoHeaders({
            headers,
            asc: sortingAsc,
            index: sortingHeaderIndex,
        });

        data = {
            ...data,
            rows: showingRows,
            headers: sortingHeaders,
        };

        return (
            <ReactCSSTransitionGroup
                transitionName="fade-in"
                transitionAppear={true}
                transitionAppearTimeout={500}
                transitionEnter={false}
                transitionLeave={false}>
                <div className={'input__table ' + className}>
                    <div className="table__content--wrapper">
                        {title && <div className="table__title content--big">{title}</div>}
                        {description && (
                            <div className="table__description content--small">{description}</div>
                        )}
                    </div>
                    <div className="table__wrapper">
                        {childrenHeader}
                        {searchEnabled && (
                            <div className="table__search">
                                <Input
                                    className="table__searchbar"
                                    iconPosition="left"
                                    defaultValue={initialSearch}
                                    placeholder="Search"
                                    onChange={_.debounce((e, data) => {
                                        const value = data.value;
                                        this.handleSearchTextChanged(e, value);
                                        if (searchPageCallback) {
                                            const trimmedValue = value.trim();
                                            if (trimmedValue !== searchString)
                                                searchPageCallback(page.number, trimmedValue);
                                        }
                                    }, 300)}>
                                    <Icon
                                        name="search"
                                        className={isSearching ? 'active' : undefined}
                                    />
                                    <input />

                                    {searchDropdownOptions.length > 0 && (
                                        <Dropdown
                                            multiple={true}
                                            className="table__search--dropdown"
                                            basic
                                            floating
                                            options={searchDropdownOptions}
                                            placeholder="All"
                                            defaultValue={searchDropdown.defaultValue}
                                            onChange={(e, data) => {
                                                data = { ...data, action: 'search' };
                                                this.handleOptionChanged(e, data);
                                            }}
                                        />
                                    )}
                                    {tableActionComponent}
                                </Input>
                            </div>
                        )}
                        <div className="table__content">
                            <Content
                                data={data}
                                Row={Row}
                                EmptyRow={EmptyRow}
                                Header={Header}
                                Footer={Footer}
                                columnCheckboxes={columnCheckboxes}
                                headerCheckboxes={headerCheckboxes}
                                links={links}
                                page={page}
                                sorting={sortingHeaderName === '_no'}
                                asc={sortingAsc}
                                columnActions={columnActions}
                                isTable={isTable}
                                isEditing={isEditing}
                                onHeaderClick={this.handleHeaderClick.bind(this)}
                            />
                        </div>
                        {paginationEnabled && (
                            <Paging
                                page={{
                                    number: pgNum,
                                }}
                                total={totalPages}
                                onPageClick={val => {
                                    setPageNumber(val, isSearching);
                                    isSearching &&
                                        searchPageCallback &&
                                        searchPageCallback(val, searchString);
                                }}
                                onNext={this.props.nextPage}
                                onPrev={this.props.prevPage}
                            />
                        )}
                        {childrenFooter}
                    </div>
                </div>
            </ReactCSSTransitionGroup>
        );
    }
}

export default compose(
    // set page number and rows number per page
    withStateHandlers(
        ({ data = {}, recordPerPage = 10, recordNumberOffset = 0, initialPage = 1 }) => ({
            // main pagination state
            page: {
                number: initialPage,
                rows: recordPerPage,
            },
            // seperate channel to keep track of search entries pagination state
            searchPage: {
                number: 1,
                rows: recordPerPage,
            },
            filteredRows: setRecordNumberIntoRows(data.rows, recordNumberOffset),
            totalPages: 0,
            sortingHeaderIndex: -1,
        }),
        {
            // stateful (no-parameter) handler from view only
            nextPage:
                (
                    { page: _page, searchPage, totalPages, isSearching, searchString },
                    { gotoPageCallback, searchPageCallback },
                ) =>
                () => {
                    const forSearching = isSearching || false;
                    const page = forSearching ? searchPage : _page;
                    const { number } = page;
                    const nextNumber = number + 1;
                    if (nextNumber > totalPages) {
                        return;
                    }
                    if (!forSearching) gotoPageCallback && gotoPageCallback(nextNumber);
                    else searchPageCallback && searchPageCallback(nextNumber, searchString);
                    const pagObj = {
                        ...page,
                        number: nextNumber,
                    };
                    return !forSearching ? { page: pagObj } : { searchPage: pagObj };
                },
            // stateful (no-parameter) handler from view only
            prevPage:
                (
                    { page: _page, searchPage, isSearching, searchString },
                    { gotoPageCallback, searchPageCallback },
                ) =>
                () => {
                    const forSearching = isSearching || false;
                    const page = forSearching ? searchPage : _page;
                    const { number } = page;
                    const prevNumber = number - 1;
                    if (prevNumber < 1) {
                        return;
                    }
                    if (!forSearching)
                        gotoPageCallback && gotoPageCallback(prevNumber, searchString);
                    else searchPageCallback && searchPageCallback(prevNumber, searchString);
                    const pagObj = {
                        ...page,
                        number: prevNumber,
                    };
                    return !forSearching ? { page: pagObj } : { searchPage: pagObj };
                },
            setPageNumber:
                ({ page: _page, searchPage }, { gotoPageCallback }) =>
                (number, forSearching) => {
                    const page = forSearching ? searchPage : _page;
                    if (!forSearching) gotoPageCallback && gotoPageCallback(number);
                    const pagObj = {
                        ...page,
                        number,
                    };
                    return !forSearching ? { page: pagObj } : { searchPage: pagObj };
                },
            // TO-CONSIDER: check is this still being used? doesn't appear so.
            setPageRows:
                ({ page }) =>
                rows => ({
                    page: {
                        ...page,
                        rows,
                    },
                }),
            setSearchString: () => str => ({
                searchString: str,
            }),
            setSortingAsc:
                () =>
                (asc = true) => ({
                    sortingAsc: asc,
                }),
            setSortingHeaderName: () => headerName => ({
                sortingHeaderName: headerName,
            }),
            setSortingHeaderIndex: () => index => ({
                sortingHeaderIndex: index,
            }),
            setFilteredRows:
                ({ page, searchPage }) =>
                (rows, forSearching) => {
                    if (forSearching === undefined) {
                        console.warn('param::forSearching is not defined, defaulting to false');
                        forSearching = false;
                    }
                    return {
                        filteredRows: rows,
                        isSearching: forSearching,
                        totalPages: Math.ceil(
                            Object.entries(rows).length / (!forSearching ? page : searchPage).rows,
                        ),
                    };
                },
        },
    ),
    withHandlers({
        filterBasedOnHeader:
            ({
                data = {},
                sortingAsc = true,

                // handler
                sortingHeaderName,
                sortingHeaderIndex,
            }) =>
            ({ headerName, rows }) => {
                const { headers } = data;
                const { sort } = headers[sortingHeaderIndex] || {};
                sortingHeaderName = headerName || sortingHeaderName;

                if (!sortingHeaderName) return rows;

                const key = sortingHeaderName;
                const startTime = new Date().getTime();

                let filteredRows;
                if (sortingHeaderIndex < 0) {
                    filteredRows = rows;
                } else {
                    filteredRows = _.sortBy(rows, row => {
                        let value = row[key];
                        if (_.isFunction(sort)) {
                            value = sort(value);
                        } else if (_.isString(sort)) {
                            value = row[sort];
                        }
                        return value;
                    });
                }

                if (!sortingAsc) {
                    filteredRows = filteredRows.reverse();
                }

                const endTime = new Date().getTime();
                log('Total sorting time [header]: ' + (endTime - startTime) + 'ms');

                return filteredRows;
            },
        filterBasedOnMatch:
            ({
                data = {},
                filteredRows,
                recordNumberOffset = 0,

                // handler
                setPageNumber,
                setFilteredRows,
            }) =>
            () => {
                let { headers, search = {}, rows } = data;
                const { match = {}, includes = [] } = search;
                let regex, matchedStr, value;
                const pattern = {};
                headers = [...headers.map(h => h.name), ...includes];
                rows = setRecordNumberIntoRows(rows, recordNumberOffset);

                if (_.isEmpty(match)) return rows;

                headers.forEach(header => {
                    const currentMatch = match[header];

                    const patternOfMatch = _.filter(currentMatch, value => {
                        return _.isString(value);
                    })
                        .map(value => {
                            value = escapeRegExp(value);
                            value = value.replace(/\ /g, '\\s');
                            return `\^${value}\$`;
                        })
                        .join('|');

                    if (patternOfMatch) pattern[header] = patternOfMatch;
                });

                if (_.isEmpty(pattern)) return rows;

                const startTime = new Date().getTime();

                // filter pattern
                const foundRows = rows.filter(r => {
                    matchedStr = true;
                    const foundInvalid = _.find(Object.entries(pattern), ([key, p]) => {
                        value = r[key];

                        if (!_.isString(value)) return false;

                        if (p.trim()) {
                            regex = new RegExp(p);
                            // if all value matched
                            matchedStr = value.match(regex);
                        }

                        return !matchedStr;
                    });

                    return !foundInvalid;
                });

                const endTime = new Date().getTime();
                log('Total sorting time [filter] : ' + (endTime - startTime) + 'ms');

                // to check and stop unnecessary update
                if (!_.isEqual(filteredRows, foundRows)) {
                    setPageNumber(1, true);
                    setFilteredRows(foundRows, true);
                }

                return foundRows;
            },
        // search only those available in data.headers
        filterBasedOnSearchString:
            props =>
            (str = '', rows) => {
                const {
                    data,
                    fuzzy = true,
                    // handlers
                    setPageNumber,
                    setFilteredRows,
                } = props;
                let { headers, search = {} } = data;
                const { includes = [], caseSensitive } = search;
                let matchedStr, regex, value;
                const strArr = str.trim().split(' ');
                headers = [...headers.map(h => h.name), ...includes];

                if (!str.trim()) {
                    setFilteredRows(rows, false);
                    return;
                }

                const startTime = new Date().getTime();

                const filteredRows = rows.filter(r => {
                    matchedStr = false;
                    if (fuzzy) {
                        let rowStr = _.map(headers, h => {
                            const value = r[h];
                            if (_.isObject(value)) return;
                            return value;
                        }).join(' ');
                        if (!caseSensitive) {
                            rowStr = rowStr.toLowerCase();
                        }
                        matchedStr = _.all(
                            _.map(strArr, s => {
                                if (!caseSensitive) {
                                    s = s.toLowerCase();
                                }
                                return rowStr.match(escapeRegExp(s));
                            }),
                        );
                    } else {
                        matchedStr = _.find(headers, c => {
                            value = r[c];

                            if (!_.isString(value)) return;

                            regex = new RegExp(`.*${escapeRegExp(str)}.*`, 'i');
                            // if value matched any of strArr
                            return value.match(regex);
                        });
                    }
                    return matchedStr;
                });

                const endTime = new Date().getTime();
                log('Total sorting time [searchString]: ' + (endTime - startTime) + 'ms');
                setPageNumber(1, true);
                setFilteredRows(filteredRows, true);
            },
    }),
    withHandlers({
        handleFilter:
            ({
                searchString,
                filterBasedOnHeader,
                filterBasedOnMatch,
                filterBasedOnSearchString,
            }) =>
            () => {
                let rows = filterBasedOnMatch();
                rows = filterBasedOnHeader({ rows });
                filterBasedOnSearchString(searchString, rows);
            },
    }),
)(TableWrapper);

TableWrapper.defaultProps = {
    className: null,

    Row: null,
    EmptyRow: null,
    Header: null,
    Footer: null,
    Content: Table,

    totalPages: 0,
    data: {},
    page: {},
    links: {},
    pageIndex: true,
    columnActions: {},
    tableActions: [],
    headerCheckboxes: {},
    columnCheckboxes: {},
    filteredRows: [],

    isTable: true,
    fuzzy: true,
};

TableWrapper.propTypes = {
    className: Proptypes.string,

    Row: Proptypes.any,
    EmptyRow: Proptypes.any,
    Header: Proptypes.any,
    Footer: Proptypes.any,
    Content: Proptypes.any,

    totalPages: Proptypes.number,
    data: Proptypes.object,
    page: Proptypes.object,
    links: Proptypes.object,
    pageIndex: Proptypes.bool,
    columnActions: Proptypes.object,
    tableActions: Proptypes.array,
    headerCheckboxes: Proptypes.object,
    columnCheckboxes: Proptypes.object,
    filteredRows: Proptypes.array,

    isTable: Proptypes.bool,
    fuzzy: Proptypes.bool,
    recordNumberOffset: Proptypes.number,
    searchEnabled: Proptypes.bool,
    childrenHeader: Proptypes.object,
    paginationEnabled: Proptypes.bool,
    childrenFooter: Proptypes.object,

    initialPage: Proptypes.number,
    gotoPageCallback: Proptypes.func,
    recordPerPage: Proptypes.number,

    initialSearch: Proptypes.string,
    searchPageCallback: Proptypes.func,
};

function setRecordNumberIntoRows(rows, recordNumberOffset) {
    rows = rows.map((r, i) => ({
        ...r,
        _no: recordNumberOffset + i + 1,
    }));

    return rows;
}

function setSortingIntoHeaders({ headers, asc, index }) {
    if (index < 0) return headers;

    headers = headers.map((h, i) => {
        let ascending, sorting;
        if (i === index) {
            ascending = asc;
            sorting = true;
        }

        return {
            ...h,
            asc: ascending,
            sorting,
        };
    });

    return headers;
}
